import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, Trash2, Sparkles, Download, Share2, Copy, CheckCircle2, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SECTION_TYPES = ["executive_summary", "methodology", "findings", "evidence", "competitors", "recommendations", "limitations", "custom"];

export default function ReportDetail() {
  const { id } = useParams();
  const { t } = useTranslation(["reports", "common", "errors"]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  const canEdit = ["owner", "admin", "researcher"].includes(role);
  const canShare = ["owner", "admin"].includes(role);

  const [report, setReport] = useState(null);
  const [labels, setLabels] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenInstr, setRegenInstr] = useState("");
  const [regenerating, setRegenerating] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/${id}`);
      setReport(data);
      if (data.sections.length && !activeId) setActiveId(data.sections[0].id);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error(t("errors:notFound"));
        navigate("/reports");
      } else { toast.error(formatApiError(err, t("errors:generic"))); }
    } finally { setLoading(false); }
  }, [id, navigate, t, activeId]);

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  // Phase 11 — labels are language-of-the-report, NOT language-of-the-UI.
  // We re-fetch when the report's language changes so the left rail +
  // "+ Section type" buttons + templated panes stay in the report's tongue.
  useEffect(() => {
    if (!report?.language) return;
    api.get(`/reports/labels?language=${encodeURIComponent(report.language)}`)
      .then(({ data }) => setLabels(data))
      .catch(() => {});
  }, [report?.language]);

  useEffect(() => {
    if (!report || !activeId) return;
    const s = report.sections.find((x) => x.id === activeId);
    setDraft((s?.content?.text) || "");
  }, [activeId, report]);

  const active = useMemo(() => report?.sections.find((s) => s.id === activeId), [report, activeId]);
  const activeTemplated = active && (active.type === "methodology" || active.type === "findings");

  const saveText = useCallback(async () => {
    if (!active) return;
    try {
      const { data } = await api.patch(`/reports/${id}/sections/${active.id}`, {
        content: { kind: "freeform", text: draft },
      });
      setReport(data);
      toast.success(t("editor.saved"));
    } catch (err) { toast.error(formatApiError(err, t("editor.saveFailed"))); }
  }, [active, draft, id, t]);

  const renameSection = async (sid, title) => {
    try {
      const { data } = await api.patch(`/reports/${id}/sections/${sid}`, { title });
      setReport(data);
    } catch (err) { toast.error(formatApiError(err, t("editor.saveFailed"))); }
  };

  const renameReport = async (title) => {
    setSavingTitle(true);
    try {
      const { data } = await api.patch(`/reports/${id}`, { title });
      setReport(data);
    } catch (err) { toast.error(formatApiError(err, t("editor.saveFailed"))); }
    finally { setSavingTitle(false); }
  };

  const publish = async () => {
    try {
      const { data } = await api.post(`/reports/${id}/publish`);
      setReport(data);
      toast.success(t("editor.published"));
    } catch (err) { toast.error(formatApiError(err, t("errors:generic"))); }
  };

  const remove = async () => {
    if (!window.confirm(t("editor.deleteConfirm"))) return;
    try { await api.delete(`/reports/${id}`); navigate("/reports"); }
    catch (err) { toast.error(formatApiError(err, t("errors:generic"))); }
  };

  const removeSection = async (sid) => {
    if (!window.confirm(t("editor.deleteSectionConfirm"))) return;
    try {
      await api.delete(`/reports/${id}/sections/${sid}`);
      await load();
    } catch (err) { toast.error(formatApiError(err, t("errors:generic"))); }
  };

  // Phase 11 — read button label from the report-language label bundle
  // (NOT from i18n). Falls back to the localised i18n string only while
  // the labels fetch is in flight.
  const sectionTypeLabel = useCallback((tp) => (
    labels?.section_titles?.[tp] || t(`sections.${tp}`)
  ), [labels, t]);

  const addSection = async (type) => {
    try {
      const { data } = await api.post(`/reports/${id}/sections`, {
        type,
        // Backend will stamp the report-language default when title is omitted.
        content: { kind: "freeform", text: "" },
      });
      await load();
      setActiveId(data.id);
    } catch (err) { toast.error(formatApiError(err, t("errors:generic"))); }
  };

  const regenerate = async () => {
    if (!active) return;
    setRegenerating(true);
    try {
      const { data } = await api.post(`/reports/${id}/regenerate-section`, {
        section_id: active.id, instructions: regenInstr || null,
      });
      setReport(data);
      setRegenOpen(false);
      setRegenInstr("");
    } catch (err) { toast.error(formatApiError(err, t("errors:generic"))); }
    finally { setRegenerating(false); }
  };

  if (loading || !report) {
    return <div className="flex items-center gap-2 py-20 text-sm text-slate-500" data-testid="report-loading"><Loader2 className="h-4 w-4 animate-spin" /> {t("editor.loading")}</div>;
  }

  return (
    <div className="space-y-6" data-testid="report-detail">
      <div className="flex items-center justify-between gap-4">
        <Link to="/reports" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900">
          <ArrowLeft className="h-4 w-4" /> {t("title")}
        </Link>
        <div className="flex items-center gap-2">
          {canShare && (
            <Button variant="outline" onClick={() => setShareOpen(true)} data-testid="report-share-button">
              <Share2 className="mr-2 h-4 w-4" /> {t("editor.share")}
            </Button>
          )}
          {canEdit && (
            <Button variant="outline" onClick={() => setExportOpen(true)} data-testid="report-export-button">
              <Download className="mr-2 h-4 w-4" /> {t("editor.export")}
            </Button>
          )}
          {canEdit && (
            <Button onClick={publish} disabled={report.status === "published"} data-testid="report-publish-button">
              {report.status === "published" ? <><CheckCircle2 className="mr-2 h-4 w-4" /> {t("editor.published")}</> : t("editor.publish")}
            </Button>
          )}
          {canEdit && (
            <Button variant="ghost" onClick={remove} className="text-rose-600 hover:bg-rose-50" data-testid="report-delete-button">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Input
          value={report.title}
          onChange={(e) => setReport({ ...report, title: e.target.value })}
          onBlur={(e) => renameReport(e.target.value)}
          className="h-12 max-w-2xl text-2xl font-bold tracking-tight"
          data-testid="report-title-input"
        />
        {savingTitle && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        <Badge variant={report.status === "published" ? "default" : "outline"}>{t(`status.${report.status}`)}</Badge>
        <Badge variant="outline" className="uppercase" data-testid="report-language-badge">{report.language}</Badge>
      </div>

      <div className="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
        <aside className="space-y-2" data-testid="report-left-rail">
          <ol className="space-y-1">
            {report.sections.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => setActiveId(s.id)}
                  className={`group flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm ${activeId === s.id ? "bg-indigo-50 text-indigo-700" : "hover:bg-slate-50 text-slate-700"}`}
                  data-testid={`section-tab-${s.type}-${s.id.slice(0,6)}`}
                >
                  <span className="truncate">{s.title}</span>
                  {canEdit && (
                    <button onClick={(e) => { e.stopPropagation(); removeSection(s.id); }} className="hidden text-slate-400 group-hover:inline">
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </button>
              </li>
            ))}
          </ol>
          {canEdit && (
            <div className="mt-3">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">{t("editor.addSection")}</Label>
              <div className="mt-1 flex flex-wrap gap-1" data-testid="add-section-buttons">
                {SECTION_TYPES.map((tp) => (
                  <button key={tp} type="button" onClick={() => addSection(tp)}
                    className="rounded-full border border-slate-200 px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-50"
                    data-testid={`add-section-${tp}`}>
                    + {sectionTypeLabel(tp)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </aside>

        <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="report-editor-pane">
          {!active ? null : activeTemplated ? (
            <TemplatedSection section={active} />
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Input
                  value={active.title}
                  onChange={(e) => setReport({ ...report, sections: report.sections.map((s) => s.id === active.id ? { ...s, title: e.target.value } : s) })}
                  onBlur={(e) => renameSection(active.id, e.target.value)}
                  className="max-w-md font-display text-lg font-semibold"
                  data-testid="section-title-input"
                />
                {canEdit && (
                  <Button variant="outline" size="sm" onClick={() => setRegenOpen(true)} data-testid="section-regen-button">
                    <Sparkles className="mr-2 h-4 w-4" /> {t("editor.regenerate")}
                  </Button>
                )}
              </div>
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={saveText}
                className="min-h-[420px] font-mono text-sm leading-relaxed"
                placeholder="…"
                data-testid="section-text-area"
              />
              <p className="text-xs text-slate-400">{t("editor.saved")} ·  auto-save on blur</p>
            </div>
          )}
        </section>
      </div>

      <Dialog open={regenOpen} onOpenChange={setRegenOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("editor.regenerate")}</DialogTitle></DialogHeader>
          <Textarea value={regenInstr} onChange={(e) => setRegenInstr(e.target.value)}
            placeholder={t("editor.regenInstructions")} data-testid="regen-instr"/>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRegenOpen(false)}>{t("create.cancel")}</Button>
            <Button onClick={regenerate} disabled={regenerating} data-testid="regen-submit">
              {regenerating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("editor.regenerating")}</> : t("editor.regenSubmit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ExportDialog open={exportOpen} setOpen={setExportOpen} reportId={id} />
      <ShareDialog open={shareOpen} setOpen={setShareOpen} reportId={id} canShare={canShare} report={report} />
    </div>
  );
}

function TemplatedSection({ section }) {
  // Phase 11 — read labels from the section's own embedded payload.
  // ``content.labels`` is stamped at write-time (or by the relocalize
  // migration) using the report's language, so we don't need a separate
  // i18n call. We never fall back to UI i18n here.
  const c = section.content || {};
  const labels = c.labels || {};
  if (section.type === "methodology") {
    const stats = c.stats || {};
    const thresholds = c.thresholds || {};
    const statLabels = labels.stats || {};
    const thresholdLabels = labels.thresholds || {};
    const headings = labels.headings || {};
    const warnings = c.warnings || [];
    return (
      <div className="space-y-5" data-testid="templated-methodology">
        <h2 className="font-display text-lg font-semibold">{section.title}</h2>
        <p className="text-xs text-slate-500">{labels.helper_text}</p>

        <div className="space-y-2">
          {headings.stats && (
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-testid="methodology-stats-heading">{headings.stats}</h3>
          )}
          <ul className="grid grid-cols-2 gap-2 text-sm text-slate-600">
            {Object.entries(stats).map(([k, v]) => (
              <li key={k} className="rounded-lg bg-slate-50 px-3 py-2" data-testid={`methodology-stat-${k}`}>
                <span className="text-slate-400">{statLabels[k] || k.replace(/_/g, " ")}:</span>{" "}
                <span className="font-medium text-slate-900">{v}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-2">
          {headings.thresholds && (
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-testid="methodology-thresholds-heading">{headings.thresholds}</h3>
          )}
          <ul className="grid grid-cols-2 gap-2 text-sm text-slate-600">
            {Object.entries(thresholds).map(([k, v]) => (
              <li key={k} className="rounded-lg bg-slate-50 px-3 py-2" data-testid={`methodology-threshold-${k}`}>
                <span className="text-slate-400">{thresholdLabels[k] || k.replace(/_/g, " ")}:</span>{" "}
                <span className="font-medium text-slate-900">{v}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-1">
          {headings.formula && (
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-testid="methodology-formula-heading">{headings.formula}</h3>
          )}
          <p className="font-mono text-xs text-slate-600">{c.confidence_formula}</p>
        </div>

        {warnings.length > 0 && (
          <div className="space-y-1">
            {headings.warnings && (
              <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-600">{headings.warnings}</h3>
            )}
            <ul className="list-disc pl-5 text-xs text-amber-700">
              {warnings.map((w, i) => <li key={i}>{typeof w === "string" ? w : (w.message || JSON.stringify(w))}</li>)}
            </ul>
          </div>
        )}
      </div>
    );
  }
  if (section.type === "findings") {
    const fs = c.findings || [];
    const fLabels = labels || {};
    return (
      <div className="space-y-3" data-testid="templated-findings">
        <h2 className="font-display text-lg font-semibold">{section.title}</h2>
        {fs.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="findings-empty">{fLabels.empty_state}</p>
        ) : (
          fs.map((f) => (
            <div key={f.id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  {fLabels.theme && f.theme_label && (
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{fLabels.theme}</p>
                  )}
                  <p className="font-display text-sm font-semibold text-slate-900">{f.theme_label || f.type}</p>
                  <p className="mt-1 text-sm text-slate-700">{f.text}</p>
                  {f.recommended_action && (
                    <p className="mt-2 text-xs italic text-slate-500">
                      <span className="not-italic font-semibold text-slate-600">{fLabels.recommended_action}:</span> {f.recommended_action}
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
                    {fLabels.evidence_count && (
                      <span><span className="font-semibold text-slate-600">{fLabels.evidence_count}:</span> {f.evidence_count || 0}</span>
                    )}
                    {f.contradictions && f.contradictions.length > 0 && fLabels.contradictions && (
                      <span className="text-rose-600"><span className="font-semibold">{fLabels.contradictions}:</span> {f.contradictions.length}</span>
                    )}
                  </div>
                </div>
                <Badge variant="outline" title={fLabels.confidence}>{Math.round((f.confidence || 0) * 100)}%</Badge>
              </div>
            </div>
          ))
        )}
      </div>
    );
  }
  return null;
}

function ExportDialog({ open, setOpen, reportId }) {
  const { t } = useTranslation(["reports"]);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState("");

  const loadHistory = useCallback(() => {
    api.get(`/reports/${reportId}/exports`).then(({ data }) => setHistory(data)).catch(() => {});
  }, [reportId]);

  useEffect(() => { if (open) loadHistory(); }, [open, loadHistory]);

  const triggerDownload = useCallback(async (exportId, fmt, suggestedName) => {
    const tok = localStorage.getItem("evidencelab.token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/exports/${exportId}/download`;
    const resp = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
    if (!resp.ok) {
      if (resp.status === 410) {
        toast.error(t("exports.gone"));
      } else {
        toast.error(t("exports.downloadFailed"));
      }
      return;
    }
    let filename = suggestedName || `report.${fmt === "markdown" ? "md" : fmt}`;
    const cd = resp.headers.get("Content-Disposition") || "";
    const m = /filename="?([^";]+)"?/i.exec(cd);
    if (m) filename = m[1];
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
    toast.success(t("exports.downloadStarted"));
  }, [t]);

  const run = async (fmt) => {
    setBusy(fmt);
    try {
      const { data } = await api.post(`/reports/${reportId}/exports`, { format: fmt });
      await triggerDownload(data.id, fmt);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("exports.downloadFailed"));
    } finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="export-dialog">
        <DialogHeader><DialogTitle>{t("exports.title")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {[
            ["pdf", t("exports.formatPdf")],
            ["docx", t("exports.formatDocx")],
            ["markdown", t("exports.formatMd")],
            ["json", t("exports.formatJson")],
          ].map(([fmt, label]) => (
            <Button key={fmt} variant="outline" disabled={busy === fmt} onClick={() => run(fmt)} data-testid={`export-format-${fmt}`}>
              {busy === fmt ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("exports.generating")}</> : <><Download className="mr-2 h-4 w-4" /> {label}</>}
            </Button>
          ))}
        </div>
        {history.length > 0 && (
          <div className="mt-3 space-y-1" data-testid="export-history-list">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("exports.history")}</p>
            <ul className="text-sm">
              {history.slice(0, 8).map((e) => (
                <li key={e.id} data-testid={`export-history-${e.id}`}
                    className="flex items-center justify-between gap-2 border-t border-slate-100 py-1.5">
                  <span className="text-slate-600">{e.format.toUpperCase()} · {t("exports.size", { kb: Math.round(e.file_size / 1024) })}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">{new Date(e.created_at).toLocaleString()}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`export-redownload-${e.id}`}
                      onClick={() => triggerDownload(e.id, e.format)}
                      className="h-7 px-2"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ShareDialog({ open, setOpen, reportId, canShare, report }) {
  const { t } = useTranslation(["reports"]);
  const [hours, setHours] = useState("168");
  const [allowFlagged, setAllowFlagged] = useState(false);
  const [creating, setCreating] = useState(false);
  const [shares, setShares] = useState([]);

  const loadShares = useCallback(() => {
    api.get(`/reports/${reportId}/shares`).then(({ data }) => setShares(data)).catch(() => {});
  }, [reportId]);

  useEffect(() => { if (open && canShare) loadShares(); }, [open, canShare, loadShares]);

  const create = async () => {
    setCreating(true);
    try {
      await api.post(`/reports/${reportId}/share`, {
        expires_in_hours: parseInt(hours, 10), allow_flagged: allowFlagged,
      });
      loadShares();
    } catch (err) { toast.error(formatApiError(err, t("errors:generic"))); }
    finally { setCreating(false); }
  };

  const revoke = async (token) => {
    try { await api.delete(`/reports/${reportId}/share/${token}`); loadShares(); }
    catch (err) { toast.error(formatApiError(err, t("errors:generic"))); }
  };

  const copyUrl = (token) => {
    const url = `${window.location.origin}/share/r/${token}`;
    navigator.clipboard.writeText(url).then(() => toast.success(t("share.copied")));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle>{t("share.title")}</DialogTitle>
        </DialogHeader>
        {report?.status !== "published" && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">{t("share.publishFirst")}</div>
        )}
        <div className="space-y-3">
          <div className="space-y-2">
            <Label>{t("share.expiresLabel")}</Label>
            <Select value={hours} onValueChange={setHours}>
              <SelectTrigger data-testid="share-expires"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="24">{t("share.expires24h")}</SelectItem>
                <SelectItem value="168">{t("share.expires7d")}</SelectItem>
                <SelectItem value="720">{t("share.expires30d")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={allowFlagged} onChange={(e) => setAllowFlagged(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600" data-testid="share-allow-flagged"/>
            {t("share.allowFlagged")}
          </label>
          <Button onClick={create} disabled={creating || report?.status !== "published"} data-testid="share-create-button">
            {creating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> …</> : t("share.create")}
          </Button>
        </div>
        {shares.length > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("share.active")}</p>
            {shares.filter((s) => !s.revoked_at).map((s) => {
              const url = `${window.location.origin}/share/r/${s.token}`;
              return (
                <div key={s.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs" data-testid={`share-row-${s.token.slice(0,6)}`}>
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-mono text-slate-600" title={url}>{url}</p>
                    <p className="text-[10px] text-slate-400">→ {new Date(s.expires_at).toLocaleString()}</p>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <button onClick={() => copyUrl(s.token)} className="rounded p-1 text-slate-500 hover:bg-slate-100" data-testid="share-copy" title={t("share.copy")}><Copy className="h-3.5 w-3.5" /></button>
                    <button onClick={() => revoke(s.token)} className="rounded p-1 text-rose-500 hover:bg-rose-50" data-testid="share-revoke" title={t("share.revoke")}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
